import {
  getBlockchainFromAddress,
  getRawAddress,
  joinAddress,
  rawAddressToLowerCase,
} from "../blockchain/address"
import type { CollectionId } from "./types"
import { toCollectionId } from "./utils"

/**
 * Readable URL segments for collection pages: `/solana/collections/claynosaurz`
 * instead of the collection address. A slug is unique within its chain; EVM
 * keys are lowercase.
 *
 * Permanent once shipped: the address URL redirects to the slug with a 308,
 * which browsers cache, so renaming or removing a slug breaks old links.
 *
 * Launched collections get theirs from the Collection Launcher, and never one
 * of these. Before adding an entry, check that no launch holds the slug
 * (`GET /collection-launcher/launches?blockchain=ALL`): a launch whose slug
 * an entry here takes falls back to its address URL.
 */
export const COLLECTION_SLUGS: Readonly<Record<string, string>> = {
  "SOLANA:6mszaj17KSfVqADrQj3o4W3zoLMTykgmV37W4QadCczK": "claynosaurz",
  "SOLANA:1yPMtWU5aqcF72RdyRD5yipmcMRC8NGNK59NvYubLkZ":
    "claynosaurz-call-of-saga",
  "SOLANA:6XHbPPTHjwiiQeigNCFdVvoV4ms7qX7Ex6KjpvkGBCrT":
    "claynosaurz-pioneer-edition",
  "SOLANA:HNXtJUwq9KLxXBYLNjUeX9uoFZG2mqyah3bP4QbbBF6h": "claymaker",
  "SOLANA:FhMPFCH2rXfkQ2AvnjuJUiBej8jgHH9D43DDkBTTL4MM": "clay",
  "SOLANA:7LxjzYdvXXDMxEmjS3aBC26ut4FMtDUae44nkHBPNVWP": "dead-king-society",
  "SOLANA:4YSo5L8mA4rdgLDKK577xhsRbKVtCHLaTcQBBXyL8JXD":
    "dead-king-society-nobles",
  "SOLANA:BUjZjAS2vbbb65g7Z1Ca9ZRVYoJscURG5L3AkVvHP9ac":
    "famous-fox-federation",
  "SOLANA:6XxjKYFbcndh2gDcsUrmZgVEsoDxXMnfsaGY6fpTJzNr": "degods",
  "SOLANA:4mKSoDDqApmF1DqXvVTSL6tu2zixrSSNjqMxUnwvVzy2": "y00ts",
  "SOLANA:3saAedkM9o5g1u5DCqsuMZuC4GRqPB4TuMkvSsSVvGQ3": "okay-bears",
  "SOLANA:J1S9H3QjnRtBbbuD4HjPV6RpRhwuk4zKbxsnCHuTgh9w": "mad-lads",
  "SOLANA:J6RJFQfLgBTcoAt3KoZFiTFW9AbufsztBNDgZ7Znrp1Q": "galactic-geckos",
  "SOLANA:SMBtHCCC6RYRutFEPb4gZqeBLUZbMNhRKaMKZZLHi7W": "smb-gen2",
  "SOLANA:8Rt3Ayqth4DAiPnW9MDFi63TiQJHmohfTWLMQFHi4KZH": "smb-gen3",
  "SOLANA:GtYP4RTJSDWTExueTnJrNhcGn8cw4BxH6yzF3HA7JwZU": "chonk-devil-gif",
  "SOLANA:CGq5TajpcznCwkwXp5u99Tv1CmU2Hkb5pEajHxkMn63n": "collector-card-club",
  // The pNFT collection trades ~11x the MPL Core one's volume (Magic Eden,
  // October 2026), so it holds the plain name.
  "SOLANA:CCryptWBYktukHDQ2vHGtVcmtjXxYzvw8XNVY64YN2Yf": "collector-crypt",
  "SOLANA:CCryptUfeFSZ3Fgc9FLeKrhLVAP67FSqi1GuVoj9CRac": "collector-crypt-core",
  "SOLANA:HMUvbjjqSyrhcU83dbP4KPH44KYKKvd1DNT9LpjMXtgS": "bobos-of-war",
  "SOLANA:aLs8rXD8NoYwgnCFzBofsGdwaocePAkzT1UFX1WmwaR": "bored-ape-solana-club",
  "SOLANA:8kYjcqW5kd77RS6twFKMXbP59bGUfC1sC3uvGkBRS6T9": "basc-v2",
  "SOLANA:5ucdKHbPfmsWowXSNfHoiYEJ55kfFmvEdHii5RtjHpiH": "wolf-capital",
  "SOLANA:TRAgFWPMRwSBF7mQ6avNFS9d8NPWkiBmXJfvHn2Hvbf": "trencher",
  "SOLANA:TRPtM4wm1CzoEhZMqt4puMBhe1AGa5kz3csPwSa3Wej": "trencher-packs",
  "SOLANA:TRNNqxjptkX8SD4r7UjvNDzXpTxu3RT7Tm55X5yi7GW": "trencher-traits",
  "SOLANA:CRittErsK51xrVktQeMeAW2cK7JNqv6Ko9Zrw26eF2cT": "critters-quest",
  "ETHEREUM:0xbc4ca0eda7647a8ab7c2061c2e118a18a936f13d": "bayc",
  "ETHEREUM:0x60e4d786628fea6478f785a6d7e704777c86a7c6": "mayc",
  "ETHEREUM:0xed5af388653567af2f388e6224dc7c4b3241c544": "azuki",
  "ETHEREUM:0xbd3531da5cf5857e7cfaa92426877b022e612cf8": "pudgy-penguins",
  "ETHEREUM:0x524cab2ec69124574082676e6f654a18df49a048": "lil-pudgys",
  "ETHEREUM:0x8a90cab2b38dba80c64b7734e58ee1db38b8992e": "doodles",
  "ETHEREUM:0x23581767a106ae21c074b2276d25e5c3e136a68b": "moonbirds",
  "ETHEREUM:0xb8ea78fcacef50d41375e44e6814ebba36bb33c4": "good-vibes-club",
  "ETHEREUM:0x5af0d9827e0c53e4799bb226655a1de152a425a5": "milady",
  "ETHEREUM:0x000000000000003607fce1ac9e043a86675c5c2f": "cryptopunks-721",
  "ETHEREUM:0xa3aee8bce55beea1951ef834b99f3ac60d1abeeb": "veefriends",
  "ROBINHOOD:0xed6e3d749aafc73b39661166a14dc20cd72c0062": "primal-apes",
  "ROBINHOOD:0xe638f58c87258ece6c0eddc46f70327663635c69": "suited-apes",
  "ROBINHOOD:0x084701d5eb3ae41680df9d1fefdcfe6cef9e498d": "franky-fish",
  "ROBINHOOD:0x9ec6c5b9f572a9b02138e553bc5f5882da735f45": "onchainhoodies",
  "ROBINHOOD:0x6581b6fa83e714956935cd1e16ac8f6f5c44c484": "monkeyhood",
  "ROBINHOOD:0x539cdd042c2f3d93ebc5be7dfff0c79f3b4fabf0": "stonkbrokers",
  "ROBINHOOD:0x797a2e030b7e49107c8f07bf0300ea9cae88ca57": "chain-mancers",
  "ETHEREUM:0x790b2cf29ed4f310bf7641f013c65d4560d28371": "otherdeed-expanded",
  // Polygon: top by volume over two of 24h, 7d and 30d (2026-10-23), and ours
  "POLYGON:0x24a11e702cd90f034ea44faf1e180c0c654ac5d9": "trump-cards",
  "POLYGON:0xe28d2d8746d855251ba677a91626009cb33aa4f9": "trump-cards-series-2",
  "POLYGON:0x9d305a42a3975ee4c1c57555bed5919889dce63f": "sandbox",
  "POLYGON:0x251be3a17af4892035c37ebf5890f4a4d889dcad": "courtyard",
  "POLYGON:0x098e1bc661398ed6858626ba5750e56ce03f9072": "cryptokhat",
  "POLYGON:0x42b4a7db1ed930198bc37971b33e86f19ce88600": "eggrypto-monsters",
  "POLYGON:0xc8a86f94af4eb42d58981b36efbe17ebcbf94312": "galaxy-habbos",
  "POLYGON:0x09aa1a14e572e6fc05f64e541699d3e0361c7f2f": "the-martians",
  "POLYGON:0x1cc25b38d7eb7711e5acc2828d7577cb8a9f2de0": "paris-hilton",
  "POLYGON:0x8463b73ea174d04db5fff567c77be968666722b4": "madballs",
  "POLYGON:0xd3ae2395b88fb918f22ddf2b8bd7042ff9732b4b": "classysassygirls-s2",
  "POLYGON:0x9d3aca725a289c6e798355592cd3dd5e43fa14a5": "derby-stars",
  "POLYGON:0x5f6ceaad29038435a764b59d716717500135a609": "derace-jockey-club",
  "POLYGON:0x9d77cb4d8371736e2a2b2bfaa677b7841cdc8fc1": "derace-horses",
  "POLYGON:0xab27db32aefba4d69e27177a4cd0734b6d1a1717": "vcitymeta-land",
  "POLYGON:0xb176c21d6b66d2fe1b0e7c697610163d28000a65": "vcitymeta-house",
  "POLYGON:0x2c7edbf8fb8baffd35caed5c7da5220ea72a7b4b": "netmarble",
  "POLYGON:0xbe71bc476369f88ab80e9d0d65c10894a64422b1": "cryptoys",
  "POLYGON:0xccb60fb93a78ba04c134afee4f7da60545d03f77":
    "avenged-sevenfold-tickets",
  "POLYGON:0x8634666ba15ada4bbc83b9dbf285f73d9e46e4c2": "chicken-derby",
  "POLYGON:0xe1c7be9a91bb376acbb7c205f1f733a3468153b4": "metavixens",
  "POLYGON:0xc29826c0d40bc904a69c536742e1951707e7d627": "polygon-x-rarible",
}

const EVM_ADDRESS = /^0x[0-9a-f]{40}$/i

const COLLECTION_ID_BY_SLUG = new Map(
  Object.entries(COLLECTION_SLUGS).map(([collectionId, slug]) => [
    joinAddress(getBlockchainFromAddress(collectionId), slug),
    toCollectionId(collectionId),
  ]),
)

/** The id slugs are keyed by: an EVM address lowercased, others as given. */
export function getCollectionSlugKey(collectionId: string): string {
  return EVM_ADDRESS.test(getRawAddress(collectionId))
    ? rawAddressToLowerCase(collectionId)
    : collectionId
}

export function getCollectionSlug(collectionId: string): string | undefined {
  return COLLECTION_SLUGS[getCollectionSlugKey(collectionId)]
}

/** The collection a slug names on a chain; `blockchain` in any case. */
export function getCollectionIdBySlug(
  blockchain: string,
  slug: string,
): CollectionId | undefined {
  return COLLECTION_ID_BY_SLUG.get(joinAddress(blockchain.toUpperCase(), slug))
}

/** Every slug the map above gives, on any chain. */
export const STATIC_COLLECTION_SLUGS: ReadonlySet<string> = new Set(
  Object.values(COLLECTION_SLUGS),
)

/** Shorter than the shortest Solana address (32), so no slug reads as one. */
export const COLLECTION_SLUG_MAX_LENGTH = 31

const SLUG_FORMAT = /^[a-z0-9]+(-[a-z0-9]+)*$/

/** Lowercase letters and digits in words joined by single hyphens. */
export function isCollectionSlug(value: string): boolean {
  return value.length <= COLLECTION_SLUG_MAX_LENGTH && SLUG_FORMAT.test(value)
}

/**
 * A slug from a collection's name: "Critters Quest" → "critters-quest".
 * Accents are dropped and a long name is cut at a word. Empty when the name
 * has no Latin letter or digit.
 */
export function toCollectionSlug(
  name: string,
  maxLength = COLLECTION_SLUG_MAX_LENGTH,
): string {
  const words = name
    .normalize("NFKD")
    .replace(/\p{M}|['’]/gu, "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
  let slug = ""
  for (const word of words) {
    const longer = slug ? `${slug}-${word}` : word
    if (longer.length > maxLength) break
    slug = longer
  }
  // Only a first word longer than the limit is cut inside the word
  return slug || (words[0]?.slice(0, maxLength) ?? "")
}
